import { PageEvents } from '@griply/types';
import {
  buildShowCompletedGoalsUpdate,
  getShowCompletedGoals,
  GoalCompletedContext,
} from '@griply/utils';
import { useLifeAreaById } from 'features/life-areas';
import { useUpdateUserSettings } from 'features/user/hooks/use-update-user-settings';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext, useParams } from 'react-router';
import { Plus, SettingsSort18 } from 'shared/assets/icons';
import { Colors, IconButton } from 'shared/components/ui/icon-button';
import { PageSectionBase } from 'shared/components/ui/page-section-base';
import {
  PopupMenu,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuToggle,
} from 'shared/components/ui/popup-menu';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { useUser } from 'shared/hooks/use-user';
import { Paths } from 'shared/routes/paths';
import { ID } from 'shared/types/id';
import { replaceUrlParams } from 'shared/utils/replace-url-params';

import { LifeAreaRoadmapAddMenu } from './components/life-area-roadmap-add-menu';
import { LifeAreaRoadmapFilterButton } from './components/life-area-roadmap-filter-button';
import { LifeAreaRoadmapOverview } from './components/life-area-roadmap-overview';
import * as Styled from './life-area-detail.style';
import { OutletContext } from './outlet-context';

// Roadmap items are top-level in the life area, so they have no parent goal.
const NO_PARENT_IDS: ID[] = [];

export const SummaryRoadmap: React.FC = () => {
  const { t } = useTranslation();
  useTrackPageVisitEvent(PageEvents.LifeAreaDetailGoals, undefined);

  const { id } = useParams();
  const { mainScrollRef } = useOutletContext<OutletContext>();
  const lifeArea = useLifeAreaById(id);
  const user = useUser();
  const updateUserSettings = useUpdateUserSettings();
  const interactionsRef = useRef<HTMLDivElement>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);

  const showCompletedGoals = getShowCompletedGoals(
    user?.settings,
    GoalCompletedContext.LifeAreaDetail,
  );

  const onShowCompletedGoals = (value: boolean) => {
    updateUserSettings(
      buildShowCompletedGoalsUpdate(GoalCompletedContext.LifeAreaDetail, value),
    );
  };

  const roadmapUrl = useMemo(
    () => replaceUrlParams(Paths.LifeAreaDetailRoadmap, { id: id! }),
    [id],
  );

  if (!lifeArea) {
    return null;
  }

  const handleOpenAddMenu = () => setIsAddMenuOpen(true);
  const handleCloseAddMenu = () => setIsAddMenuOpen(false);
  const handleOpenSortMenu = () => setIsSortMenuOpen(true);
  const handleCloseSortMenu = () => setIsSortMenuOpen(false);

  return (
    <PageSectionBase
      title={t('pages.life-area-detail.sections.roadmap.label')}
      url={roadmapUrl}
      interactions={
        <Styled.InteractionsContainer ref={interactionsRef}>
          <IconButton
            icon={Plus}
            ref={addButtonRef}
            onClick={handleOpenAddMenu}
            color={Colors.Default}
          />
          <LifeAreaRoadmapFilterButton lifeArea={lifeArea} />
          <IconButton
            icon={SettingsSort18}
            onClick={handleOpenSortMenu}
            color={Colors.Default}
          />
          {isAddMenuOpen && (
            <PopupMenu
              referenceElement={addButtonRef}
              onOutside={handleCloseAddMenu}
              position="bottom-end"
            >
              <LifeAreaRoadmapAddMenu
                parentIds={NO_PARENT_IDS}
                lifeAreaId={lifeArea.id}
                onClose={handleCloseAddMenu}
              />
            </PopupMenu>
          )}
          {isSortMenuOpen && (
            <PopupMenu
              referenceElement={interactionsRef}
              onOutside={handleCloseSortMenu}
              position="bottom-end"
            >
              <PopupMenuList>
                <PopupMenuListItem>
                  <PopupMenuToggle
                    label={t('general.labels.goals.show-completed-goals')}
                    checked={showCompletedGoals}
                    onChange={onShowCompletedGoals}
                  />
                </PopupMenuListItem>
              </PopupMenuList>
            </PopupMenu>
          )}
        </Styled.InteractionsContainer>
      }
      noPadding
    >
      <LifeAreaRoadmapOverview
        lifeArea={lifeArea}
        hideCompleted={!showCompletedGoals}
        scrollElement={mainScrollRef}
        hideArchived
      />
    </PageSectionBase>
  );
};
